import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [50.4, 2.7]; // Artois

const dot = (color, size = 14, pulse = false) => L.divIcon({
  className: '',
  html: `<div style="position:relative;width:${size}px;height:${size}px;">${pulse ? `<div style="position:absolute;inset:-6px;border-radius:50%;background:${color};opacity:.25;animation:tract-pulse 1.6s ease-out infinite;"></div>` : ''}<div style="position:absolute;inset:0;background:${color};border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);"></div></div>`,
  iconSize: [size, size], iconAnchor: [size / 2, size / 2],
});

/**
 * Imperative Leaflet map drawing GPS routes.
 * routes: [{ id, points: [{lat,lng}], color, label, live?: bool, dashed?: bool }]
 * focusId: route id to fit on; otherwise fits all routes. follow: keep centering on the last point of live routes.
 */
export function RouteMap({ routes = [], focusId = null, follow = false, height = 320, className = '', onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef('');

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { center: DEFAULT_CENTER, zoom: 11, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap', maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    containerRef.current.leafletMap = map; // exposed for debugging/tests
    setTimeout(() => map.invalidateSize(), 50);
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; fittedRef.current = ''; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return undefined;
    layer.clearLayers();
    const bounds = [];
    let focusBounds = null;
    routes.forEach((r) => {
      const latlngs = (r.points || []).map((p) => [p.lat, p.lng]);
      if (!latlngs.length) return;
      const line = L.polyline(latlngs, { color: r.color || '#0f766e', weight: r.live ? 5 : 4, opacity: 0.9, dashArray: r.dashed ? '6 8' : null, lineJoin: 'round' }).addTo(layer);
      if (r.label) line.bindTooltip(r.label, { sticky: true });
      if (onSelect) line.on('click', () => onSelect(r.id));
      L.marker(latlngs[0], { icon: dot(r.color || '#0f766e', 10) }).addTo(layer);
      const last = latlngs[latlngs.length - 1];
      L.marker(last, { icon: dot(r.color || '#0f766e', r.live ? 16 : 12, !!r.live) }).addTo(layer).bindTooltip(r.live ? `${r.label || 'En cours'} — position actuelle` : (r.label || ''), { direction: 'top' });
      bounds.push(...latlngs);
      if (focusId && r.id === focusId) focusBounds = latlngs;
    });
    const key = `${focusId || 'all'}:${routes.map((r) => r.id).join(',')}`;
    if (follow) {
      const live = routes.find((r) => r.live && r.points?.length);
      if (live) {
        const p = live.points[live.points.length - 1];
        map.invalidateSize();
        map.setView([p.lat, p.lng], Math.max(map.getZoom(), 16), { animate: true });
        return undefined;
      }
    }
    const target = focusBounds || bounds;
    if (target.length && fittedRef.current !== key) {
      // Containers may still be laying out (dialogs, tabs): size the map before fitting.
      const timer = setTimeout(() => {
        if (!mapRef.current) return;
        fittedRef.current = key;
        map.invalidateSize();
        if (target.length === 1) map.setView(target[0], 16);
        else map.fitBounds(L.latLngBounds(target), { padding: [24, 24], maxZoom: 17 });
      }, 80);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [routes, focusId, follow, onSelect]);

  return (
    <div className={`relative overflow-hidden rounded-xl border border-border ${className}`} style={{ height }} data-testid="route-map">
      <style>{'@keyframes tract-pulse{0%{transform:scale(.6);opacity:.5}100%{transform:scale(2.2);opacity:0}}'}</style>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

export const formatDistance = (m) => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m || 0)} m`);
export const formatDuration = (s) => {
  const total = Math.max(0, Math.round(s || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}m${String(sec).padStart(2, '0')}`;
};
